/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DiagramModules
 */

import { timeParse } from 'd3';
import { IVsLogsUpdatableParams } from 'ng/modules/vs-logs';
import {
    TTimestampFormatFromApi,
    TTimestampFormatFromApiWithMicroS,
} from 'ng/modules/diagram/components/avi-bar-graph/avi-bar-graph.types';

/**
 * Represent desired target number of divisions on x-axis.
 *
 * Graph api accepts 3 request parameters which work together:
 * end: timestamp of very end of graph, typically the timestamp when api request is made,
 * duration: total time in seconds from beginning to end of graph,
 * step: how long in seconds each response entry should represent.
 *
 * end and duration together tell api the desired time range for the query.
 * The UI wants a max of 120 divisions on the x-axis, so step is then calculated
 * by dividing duration by desiredXAxisSplits.
 */
const desiredXAxisSplits = 120;

/**
 * Calculate 'step' api request param for given duration.
 * See description of const 'desiredXAxisSplits' above for more detail.
 */
export function calculateStep(
    duration: IVsLogsUpdatableParams['duration'],
    xSplits: number = desiredXAxisSplits,
): IVsLogsUpdatableParams['step'] {
    return Math.ceil(duration / xSplits).toString();
}

/**
 * Convert input to readable/avi format time.
 */
export function convertTimeToTTimestampFormatFromApi(time: number): Date {
    if (typeof time !== 'number' || !Number.isFinite(time)) {
        throw new Error('Time input must be a valid finite number');
    }

    const parsed = timeParse('%Q')(Math.floor(time).toString());

    if (parsed === null) {
        throw new Error('Failed to parse time input');
    }

    return parsed;
}

/**
 * Standard date parser returns milliseconds unix time from time-format.
 */
export function getUnixTimeInMilliseconds(
    input: TTimestampFormatFromApi | TTimestampFormatFromApiWithMicroS | Date,
): number {
    if (input == null) {
        throw new Error('Timestamp input cannot be null or undefined');
    }

    return Date.parse(input.toString());
}
